
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { Stages } from '@atlaskit/progress-tracker';
import React , { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import useLocalStorage from '@/components/common/hooks/useLocalStorage';
import { InstallationDetails } from '@/components/common/types';

import Progress from '../common/Progress';

import { InstallationDetailsModal } from './InstallationDetailsModal';

const containerStyles = xcss({
    display: 'flex', 
    justifyContent: 'space-around',
});

const childContainerStyles = xcss({
    display: 'flex',
    justifyContent: 'center',
    minHeight: '30vh',
});

interface OnboardingLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  onNext?: () => Promise<boolean | { shouldProceed: boolean; searchParams?: Record<string, string> }> | boolean | { shouldProceed: boolean; searchParams?: Record<string, string> }; // Optional callback that returns true if navigation should proceed, or an object with shouldProceed and optional searchParams
  onNextLabel?: string;
  isNextDisabled?: () => boolean;
}

const onboardingSteps: Stages = [
  {
    id: 'welcome',
    label: 'Get started',
    percentageComplete: 0,
    status: 'unvisited',
    href: '/slack/onboard/welcome',
  },
  {
    id: 'installation',
    label: 'Install app',
    percentageComplete: 0,
    status: 'unvisited',
    href: '/slack/onboard/installation',
  },
  {
    id: 'sitedetails',
    label: 'Add site',
    percentageComplete: 0,
    status: 'unvisited',
    href: '/slack/onboard/sitedetails',
  },
  {
    id: 'complete',
    label: 'Finish',
    percentageComplete: 0,
    status: 'unvisited',
    href: '/slack/onboard/complete',
  },
];

export const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({ 
  children, 
  currentStep,
  onNext,
  onNextLabel,
  isNextDisabled,
}) => {
  const history = useHistory();
  const [installationDetails, _] = useLocalStorage<InstallationDetails | null>('installation_details', null);
  const [showInstallationModal, setShowInstallationModal] = useState(false);

  useEffect(() => {
    if(window.location.pathname.includes('/slack/onboard/complete') || window.location.pathname.includes('/slack/onboard/sitedetails')) {
      return setShowInstallationModal(false);
    }

    if(installationDetails && installationDetails.enterpriseId && installationDetails.teamId) {
      return setShowInstallationModal(true);
    }
  }, [installationDetails]);

  const handleReinstall = () => {
    setShowInstallationModal(false);
    // Clear installation details and continue with fresh installation
    localStorage.removeItem('installation_details');
    // Optionally redirect to installation step
    history.push('/slack/onboard/installation');
  };

  const handleManageSiteDetails = () => {
    setShowInstallationModal(false);
    // Navigate to site details with existing installation details
    const url = new URL('/slack/onboard/sitedetails', window.location.origin);
    url.searchParams.set('enterpriseId', installationDetails!.enterpriseId);
    url.searchParams.set('teamId', installationDetails!.teamId);
    history.push(url.pathname + url.search);
  };

  // Update progress based on current step
  const updatedSteps = onboardingSteps.map((step, index) => {
    let status: 'unvisited' | 'current' | 'visited' = 'unvisited';
    let percentageComplete = 0;

    if (index < currentStep) {
      status = 'visited';
      percentageComplete = 100;
    } else if (index === currentStep) {
      status = 'current';
      percentageComplete = 50;
    }

    return {
      ...step,
      status,
      percentageComplete,
    };
  });

  const handleNext = async () => {
    if (currentStep < onboardingSteps.length) {
      let searchParams: Record<string, string> = {};
      
      // Call onNext if provided
      if (onNext) {
        try {
          const result = await onNext();
          
          // Handle both boolean and object return types
          let shouldProceed: boolean;
          if (typeof result === 'boolean') {
            shouldProceed = result;
          } else {
            shouldProceed = result.shouldProceed;
            if (result.searchParams) {
              searchParams = result.searchParams;
            }
          }
          
          if (!shouldProceed) {
            return; // Don't navigate if onNext returns false
          }
        } catch (error) {
          console.error('Error in onNext callback:', error);
          return; // Don't navigate if onNext throws an error
        }
      }
      
      const nextHref = onboardingSteps[currentStep + 1]?.href;
      if (nextHref) {
        // Build URL with search params if provided
        const url = new URL(nextHref, window.location.origin);
        Object.entries(searchParams).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
        
        history.push(url.pathname + url.search);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevHref = onboardingSteps[currentStep - 1]?.href;
      if (prevHref) {
        history.push(prevHref);
      }
    }
  };

  const isFirstStep = currentStep === 0;

  return (
    <Box>
      <Box paddingBlock="space.300">
        <Progress items={updatedSteps} />
      </Box>
      
      <Box paddingBlock="space.400" xcss={childContainerStyles}>
        {children}
      </Box>

      <Box 
        as="div"
        xcss={containerStyles}
      >
        {!isFirstStep && (
          <Button 
            appearance="subtle"
            onClick={handlePrevious}
          >
            Previous
          </Button>
        )}
        
        <Button 
          appearance="primary"
          isDisabled={isNextDisabled ? isNextDisabled() : false}
          onClick={handleNext}
        >
          {onNextLabel ? onNextLabel : 'Next'}
        </Button>
      </Box>

      <InstallationDetailsModal
        isOpen={showInstallationModal}
        onReinstall={handleReinstall}
        onManageSiteDetails={handleManageSiteDetails}
      />
    </Box>
  );
}; 